<template>
  <VdPage class="abtests-list-page" title="A/B Tests" :subtitle="`${abtests.length}`">
    <template #actions>
      <VdSwitch v-model="expandAll" active-text="Expand all" />
      <InputText
        ref="searchInputRef"
        v-model="searchTerm"
        class="search-input"
        placeholder="Looking for something..."
        @focusout="forceFocusOnSearchInput"
        @input="forceBlur = false"
      />
      <vd-button
        v-if="inWipVersion && canWriteAbtests"
        class="new-abtest-button"
        type="secondary"
        skin="blue"
        icon-left="fa:add"
        @click="onNewAbtest"
      >
        New A/B Test
      </vd-button>
    </template>

    <div class="abtests-list">
      <div class="abtests-list-wrapper">
        <div :class="['layer-actions', { 'with-tabs': !!filteredAbtests?.length }]">
          <AbtestLayerSelect v-model="selectedLayer" :game-id="gameId" :version-id="versionId" is-filter />
        </div>
        <el-tabs
          v-model="activeAbTestCategory"
          class="abtests-tabs"
          @tab-click="({paneName}) => refetchWithState(paneName as AbtestState ?? 'All')"
        >
          <el-tab-pane v-for="state in Object.values(abtestsStates)" :key="state" :label="state" :name="state">
            <!-- <h1 class="section-title">
              <span class="title">Archived A/B Tests</span>
              <span class="subtitle">{{ archivedAbtestsCount }}</span>
            </h1> -->
            <section v-if="loading" class="abtests-items-loader">
              <ProgressLoader />
            </section>
            <section
              v-else-if="filteredAbtests && !!Object.keys(filteredAbtests).length && !loading"
              class="abtests-items"
            >
              <template v-for="(abtestsByLayer, key) in filteredAbtests" :key="key">
                <div class="section-title">
                  <VdIcon name="layer-group" store="fa" size="large" />
                  <div class="title">{{ key }}</div>
                  <CopyButton :id="abtestsByLayer[0].layer.id" />
                </div>
                <ABTestsListItem
                  v-for="item in abtestsByLayer"
                  :id="item.id"
                  :key="item.id"
                  :in-wip-version="inWipVersion"
                  :abtest="item"
                  :game-id="gameId"
                  :version-id="versionId"
                  :expanded="expandAll"
                  :show-test-button="canReadTesters && (inWipVersion || inLiveVersion)"
                  :offers-by-id="offersById"
                  :campaign-names-by-id="campaignNamesById"
                  @edit="onEdit"
                  @edit-treatment="onEditTreatment"
                  @edit-cohorts="onEditCohorts"
                  @draft="onMarkedAsDraft"
                  @ready="onMarkedAsReady"
                  @delete="onDelete"
                  @terminating="onMarkedAsTerminating"
                  @cancel-terminating="onMarkedAsCancelTerminating"
                  @duplicate="onDuplicate"
                  @attach-tester="(cohortId) => onOpenTestDeviceSelection(item.id, cohortId)"
                />
              </template>
            </section>
            <vd-empty v-else :title="searchTerm ? 'not found' : `no  ${state !== 'All' ? state : ''} A/B Test`">
              <div v-if="searchTerm">
                <p>Your search for an A/B Test has no results</p>
              </div>
              <div v-else>
                <p>Sorry, there is no data.</p>
                <p v-if="inWipVersion">Refresh the page or try the following option:</p>
              </div>
              <template v-if="!searchTerm && inWipVersion && canWriteAbtests" #actions>
                <vd-button type="secondary" skin="blue" icon-left="fa:add" @click="onNewAbtest">New A/B Test</vd-button>
              </template>
            </vd-empty>
          </el-tab-pane>
        </el-tabs>
      </div>
    </div>

    <AbtestTestDeviceAssignationDialog
      :visible="showTestDeviceAssignationDialog"
      :game-id="gameId"
      :version-id="versionId"
      :abtest-id="selectedAbtest"
      :cohort-id="selectedCohort"
      @close="onCloseTestDeviceAssignationDialog"
    />
  </VdPage>
</template>

<script lang="ts" setup>
import VdIcon from "@/components/VdIcon/VdIcon.vue";
import { VdButton, VdPage, VdSwitch } from "@voodoo.io/voodoo-ui";
import { onClickOutside } from "@vueuse/core";
import InputText from "primevue/inputtext";
import { computed, ref } from "vue";
/**
 * Composables imports
 */
import { useRouter } from "vue-router";

import CopyButton from "@/components/CopyButton/CopyButton.vue";
import ProgressLoader from "@/components/ProgressLoader/ProgressLoader.vue";
import {
  AbtestFragment,
  AbtestState,
  queryNames,
  useAbtestsListPageDeleteMutation,
  useAbtestsListPageDuplicateMutation,
  useAbtestsListPageQuery,
  useAbtestsListPageUpdateStateMutation,
  useMutationResponse,
} from "@/graphql";
import useFeatureFlags from "@/hooks/useFeatureFlags";
import useMessages from "@/hooks/messages";
import useSearchFilter from "@/hooks/search-filter";
import usePermissions from "@/hooks/use-permissions";
import links from "@/router/links";

/**
 * Composition components
 */
import AbtestLayerSelect from "../components/AbtestLayerSelect/AbtestLayerSelect.vue";
import AbtestTestDeviceAssignationDialog from "../components/AbtestTestDeviceAssignationDialog/AbtestTestDeviceAssignationDialog.vue";
import useTreatmentOffersData from "@/modules/Offers/hooks/useTreatmentOffersData";
import ABTestsListItem from "./components/AbtestsListItem/AbtestsListItem.vue";

const props = defineProps({
  gameId: {
    type: String,
    required: true,
  },

  versionId: {
    type: String,
    required: true,
  },
});

/**
 * Setup page
 */
const router = useRouter();
const { canWriteAbtests, canReadTesters } = usePermissions();
const { confirm } = useMessages();

// Offer/campaign lookups for the cohort treatments display, fetched once here instead of per treatment.
const { offersById, campaignNamesById } = useTreatmentOffersData(props.gameId, props.versionId);

const expandAll = ref(false);

const searchInputRef = ref();
const forceBlur = ref(false);

onClickOutside(searchInputRef, () => {
  forceBlur.value = true;
  searchInputRef.value.$el.blur();
});

const forceFocusOnSearchInput = () => {
  if (searchInputRef.value && !forceBlur.value) {
    searchInputRef.value.$el.focus();
  }
};

/**
 * Tabs setup
 */
const activeAbTestCategory = ref("All");

/**
 * A/B Tests current State setup
 */
const loadedState = ref("All");
const abtestsStates = ["All", ...Object.keys(AbtestState)];
const abtestsRefetchLoading = ref([false]);

/**
 * Initiating GraphQL query for AbtestsListPage
 */
const { result, refetch: toggleQueryForState } = useAbtestsListPageQuery(
  { gameId: props.gameId, versionId: props.versionId, states: [] },
  () => ({
    fetchPolicy: "cache-and-network",
  })
);

const abTestsFirstLoading = computed(() => !result.value?.game?.version?.abtests);

const abtests = computed(() => result.value?.game?.version?.abtests ?? []);

const inWipVersion = computed(() => result.value?.game?.version?.isWip ?? false);
const inLiveVersion = computed(() => result.value?.game?.version?.isLive ?? false);

/**
 * Filter the features from the searchTerm
 */
const { searchTerm, filteredItems: baseFilteredAbtests } = useSearchFilter(abtests, ["name", "layer.name"]);

/**
 * Filtering by layers (selecting all layers by default)
 */
const selectedLayer = ref("all");

/**
 * Let's filter abtests by layers (if the user has selected a layer)
 * and group them by layer also
 */
const filteredAbtests = computed(() => {
  const filteredAbtestsByLayer = baseFilteredAbtests?.value
    ?.filter((ab) => {
      if (!["all", ""].includes(selectedLayer.value)) {
        return ab.layer?.id === selectedLayer.value;
      }
      return true;
    })
    .reduce((acc, curr) => {
      if (curr?.layer?.name) {
        acc[curr.layer.name] = acc[curr.layer.name] || [];
        acc[curr?.layer?.name].push(curr);
      }
      return acc;
    }, {} as Record<string, AbtestFragment[]>);

  /**
   * If the current state in the tab is "Archived", then let's sort correctly the A/B Tests (sorting by the version they were stopped on)
   * TODO - Check if this part is still useful as the archived abtests are now only the one of the previous version (N - 1 only)
   */
  if (loadedState.value === AbtestState.Archived) {
    Object.values(filteredAbtestsByLayer).map((ab) =>
      ab.sort((a, b) => {
        if (!a.stoppedOnVersion) {
          return -1;
        }
        if (!b.stoppedOnVersion) {
          return 1;
        }
        return b.stoppedOnVersion.historySeq - a.stoppedOnVersion.historySeq;
      })
    );
  }

  const sortedEntries = Object.entries(filteredAbtestsByLayer).map(([layer, abs]) => [
    layer,
    abs.sort((a, b) => {
      if (!a.state || !b.state) {
        return 0;
      }

      const map = new Map<AbtestState, number>();
      map.set(AbtestState.Published, 0);
      map.set(AbtestState.Pending, 1);
      map.set(AbtestState.Terminating, 2);
      map.set(AbtestState.Draft, 3);
      map.set(AbtestState.Archived, 4);

      if (map.get(a.state) < map.get(b.state)) {
        return -1;
      }
      if (map.get(a.state) > map.get(b.state)) {
        return 1;
      }
      return 0;
    }),
  ]);

  return Object.fromEntries(sortedEntries);
});

/**
 * Load Abtests accordingly to their state
 */

/**
 * * This method is very important to understand as we fetch the base state in a first time (for now it's "Published"),
 * * Then each time a different tab is clicked we refetch the "new state clicked" data.
 * @param state
 */
const refetchWithState = async (state: AbtestState | "All" = AbtestState.Published) => {
  let states = [];
  /**
   * If the tab clicked is all then select all the states (except archived),
   * otherwise select the right state
   */
  if (state !== "All") {
    loadedState.value = state.toUpperCase() as AbtestState;
    // loadedState.value = [...new Set(loadedState.value)]
    states = [loadedState.value];
  } else {
    states = Object.values(AbtestState).filter((s) => s !== AbtestState.Archived);
  }

  abtestsRefetchLoading.value.push(true);
  let newStateLoaded;

  try {
    newStateLoaded = await toggleQueryForState({
      gameId: props.gameId,
      versionId: props.versionId,
      states: states as AbtestState[],
    });
  } catch (e) {
    /** If there are more than two truthy loading, it means that at least another AbtestsListPageQuery is running, so let it run, otherwise reset it */
    if (abtestsRefetchLoading.value.filter((loading) => !!loading).length === 1) {
      abtestsRefetchLoading.value = [false];
    }
  }

  if (newStateLoaded) {
    activeAbTestCategory.value = state as AbtestState;
    abtestsRefetchLoading.value = [false];
  }
};

// const debouncedRefetch = useDebounceFn(refetchWithState, 1000, { maxWait: 5000 });

const onNewAbtest = () => {
  router.push({
    name: links.games.abtests.new,
    params: {
      gameId: props.gameId,
      versionId: props.versionId,
      selectedLayer: selectedLayer.value !== "all" ? selectedLayer.value : null,
    },
  });
};

/**
 * Delete menu action
 */
const { flags } = useFeatureFlags(props.gameId);
const { mutate: deleteAbtest } = useAbtestsListPageDeleteMutation({
  refetchQueries: [queryNames.AbtestsListPage, queryNames.AbtestsLayerSelect],
});

const onDelete = async (abtestId: string) => {
  const confirmed = await confirm("Do you really want to delete this A/B Test?", { type: "warning" });
  if (confirmed) {
    const response = await deleteAbtest({
      gameId: props.gameId,
      versionId: props.versionId,
      abtestId,
      useMultiLink: flags.value.offerMultiLinkABTest.enabled,
    });

    useMutationResponse(response, (resp) => resp?.deleteAbtest);
  }
};

/**
 * Edit menu action
 */
const { mutate: updateState, loading: updateStateLoading } = useAbtestsListPageUpdateStateMutation({
  refetchQueries: [queryNames.AbtestsListPage],
});

const onEdit = (abtestId: string) => {
  router.push({
    name: links.games.abtests.edit,
    params: { gameId: props.gameId, versionId: props.versionId, abtestId },
  });
};

const onEditTreatment = (abtestId: string) => {
  router.push({
    name: links.games.abtests.dynamicTreatment,
    params: { gameId: props.gameId, versionId: props.versionId, abtestId },
  });
};

const onEditCohorts = (abtestId: string) => {
  router.push({
    name: links.games.abtests.cohorts.list,
    params: { gameId: props.gameId, versionId: props.versionId, abtestId },
  });
};

const setAbtestState = (abtestId: string, state: AbtestState) =>
  updateState({
    gameId: props.gameId,
    versionId: props.versionId,
    abtestId,
    state,
  });

const onMarkedAsDraft = async (abtestId: string) => {
  const response = await setAbtestState(abtestId, AbtestState.Draft);
  useMutationResponse(response, (resp) => resp?.updateAbtestState);
};

const onMarkedAsReady = async (abtestId: string) => {
  const response = await setAbtestState(abtestId, AbtestState.Pending);
  useMutationResponse(response, (resp) => resp?.updateAbtestState);
};

const { mutate: duplicateAbTest, loading: duplicateAbTestLoading } = useAbtestsListPageDuplicateMutation({
  refetchQueries: [queryNames.AbtestsListPage],
});

const onDuplicate = async (toDuplicateId: string) => {
  const confirmed = await confirm(
    "The copy of this A/B test will be mutable since we do not manage immutability anymore in VoodooTune.",
    { type: "warning" }
  );
  if (confirmed) {
    const response = await duplicateAbTest({
      gameId: props.gameId,
      versionId: props.versionId,
      abtestId: toDuplicateId,
    });
    const duplicateQueryResult = useMutationResponse(response, (resp) => resp?.duplicateAbtest);

    if (duplicateQueryResult?.success && duplicateQueryResult.duplicatedAbtestId) {
      router.push({
        name: links.games.abtests.edit,
        params: { gameId: props.gameId, versionId: props.versionId, abtestId: duplicateQueryResult.duplicatedAbtestId },
      });
    }
  }
};

const onMarkedAsCancelTerminating = async (abtestId: string) => {
  const response = await setAbtestState(abtestId, AbtestState.Published);
  useMutationResponse(response, (resp) => resp?.updateAbtestState);
};

const onMarkedAsTerminating = async (abtestId: string) => {
  const confirmed = await confirm("The A/B Test will be marked for archiving and stopped after the next publication.");
  if (confirmed) {
    const response = await setAbtestState(abtestId, AbtestState.Terminating);
    useMutationResponse(response, (resp) => resp?.updateAbtestState);
  }
};

/**
 * Testers attachment
 */

const showTestDeviceAssignationDialog = ref(false);
const selectedAbtest = ref("");
const selectedCohort = ref("");

const onOpenTestDeviceSelection = async (abtestId: string, cohortId: string) => {
  showTestDeviceAssignationDialog.value = true;
  selectedAbtest.value = abtestId;
  selectedCohort.value = cohortId;
};

const onCloseTestDeviceAssignationDialog = () => {
  showTestDeviceAssignationDialog.value = false;
  selectedAbtest.value = "";
  selectedCohort.value = "";
};

const loading = computed(
  () =>
    updateStateLoading.value ||
    duplicateAbTestLoading.value ||
    abtestsRefetchLoading.value.some((refetch) => !!refetch) ||
    // This is done because the loading of the first request is not working properly (when using the refetch it's always there)
    abTestsFirstLoading.value
);
</script>

<style lang="scss" scoped>
.abtests-list-page {
  .no-data {
    @extend %no-data;
    font-size: font("XL");
    padding-left: size("M");
  }

  .section-title {
    &:first-of-type {
      margin-top: size("M");
    }

    margin-top: size("L");
    display: inline-flex;
    align-items: center;
    font-size: font("L");
    column-gap: size("S");
    color: $soft-black;

    svg {
      color: $soft-black;
    }
  }

  .abtests-list-wrapper {
    position: relative;

    :deep(.el-tabs) {
      position: absolute;
      top: 0;
      width: 100%;
    }

    .layer-actions {
      text-align: right;
      position: absolute;
      right: 0;
      z-index: 999;

      &.with-tabs {
        position: absolute;
        right: 0;
      }
    }
  }

  .abtests-items {
    width: 100%;
    height: 100%;
    padding-bottom: 5rem;
  }

  .abtests-items-loader {
    display: flex;
    min-height: 200px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    row-gap: 0;
  }

  .abtests-tabs {
    :deep(.el-tabs__header.is-top) {
      display: inline-block;
    }
  }

  :deep(.el-tabs) {
    .el-tabs__nav-wrap:after {
      background-color: transparent;
      height: 4px;
    }

    .el-tabs__item {
      font-size: font("ML");
      font-weight: 600;
      line-height: 32px;

      &.is-active {
        color: $slate;
      }
    }

    .el-tabs__active-bar {
      height: 4px;
      background-color: $intense-blue;
    }
  }
}
</style>
